import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LightColors, DarkColors, ColorScheme } from '../theme/colors';
import { FontFamily, FontSize, FontWeight, LineHeight } from '../theme/typography';
import { Spacing } from '../theme/spacing';
import { Radii } from '../theme/radii';
import { Shadows } from '../theme/shadows';
import { Motion } from '../theme/motion';
import { Platform } from 'react-native';

interface Theme {
  colors: ColorScheme;
  font: typeof FontFamily;
  fontSize: typeof FontSize;
  fontWeight: typeof FontWeight;
  lineHeight: typeof LineHeight;
  spacing: typeof Spacing;
  radii: typeof Radii;
  shadows: typeof Shadows;
  motion: typeof Motion;
  isDark: boolean;
}

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function buildTheme(isDark: boolean): Theme {
  return {
    colors: isDark ? DarkColors : LightColors,
    font: FontFamily,
    fontSize: FontSize,
    fontWeight: FontWeight,
    lineHeight: LineHeight,
    spacing: Spacing,
    radii: Radii,
    shadows: Shadows,
    motion: Motion,
    isDark,
  };
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem('theme').then(val => {
      if (val === 'dark') setIsDark(true);
    });
  }, []);

  // Keep the document's data-theme attribute in sync with isDark on EVERY change
  // (initial restore + toggle), so the html/body background CSS in index.html
  // (and any :root selectors) track the active theme — not just on toggle.
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    }
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark(d => {
      const next = !d;
      AsyncStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  }, []);

  const theme = buildTheme(isDark);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Pins the LIGHT theme for its subtree, ignoring the user's global dark/light
 * preference. Wrap the auth flow (login · signup · forgot/reset password ·
 * email verification) with this so those screens are always rendered in light
 * mode. `toggleTheme` is a deliberate no-op inside this scope.
 */
export function ForcedLightThemeProvider({ children }: { children: React.ReactNode }) {
  // While the auth flow is mounted, pin the web document root to light too, so
  // any html/body-level styling can't bleed dark through behind the screens.
  // Restore whatever was there on unmount (back to the user's real preference).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const root = document.documentElement;
    const prev = root.getAttribute('data-theme');
    root.setAttribute('data-theme', 'light');
    return () => {
      if (prev) root.setAttribute('data-theme', prev);
    };
  }, []);

  const theme = buildTheme(false);

  return (
    <ThemeContext.Provider value={{ theme, isDark: false, toggleTheme: () => {} }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

// Non-throwing accessor for components that may render outside the provider
// (e.g. the top-level error boundary which wraps ThemeProvider). Falls back to
// the default light theme when no provider is present.
const fallbackTheme: Theme = buildTheme(false);

export function useThemeSafe(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  return { theme: fallbackTheme, isDark: false, toggleTheme: () => {} };
}
